"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Heart,
  Sparkles,
  Crown,
  Users,
  MessageSquare,
  Shield,
  TrendingUp,
  Target,
  Award,
  ArrowRight,
  Mic,
  BookOpen,
  CheckCircle2,
  Eye,
  Compass,
  Briefcase,
  Building2,
  Trophy,
  GitBranch,
  Flame,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";
import { CounterBlock } from "@/lib/blocks/counter-block";

const PROJECT_PILLARS = [
  { num: "01", title: "Signal-Based Selection", body: "Replace confidence-based hiring with performance-based evaluation. The signals that predict team output — communication discipline, risk awareness, collaborative depth, retention stability, ego management — become the criteria that matter.", icon: <Compass className="size-5" />, metric: "5", metricLabel: "Performance signals", accent: "emerald" as const },
  { num: "02", title: "Architectural Recruitment", body: "Build the pipeline rather than recruit from the visible pool. The talent arbitrage is a structural advantage that compounds over hiring cycles. The architecture that selects, develops, and retains the talent competitors ignore is the moat.", icon: <Building2 className="size-5" />, metric: "300+", metricLabel: "Elite clients", accent: "red" as const },
  { num: "03", title: "Communication Protocol", body: "Establish explicit communication standards that elevate information flow. Handoffs lose less data, escalations reach decision-makers faster, and the surfacing of uncomfortable truths becomes the operating default rather than the exception.", icon: <MessageSquare className="size-5" />, metric: "Faster", metricLabel: "Problem identification", accent: "purple" as const },
  { num: "04", title: "Risk Architecture", body: "Embed the do-no-harm discipline at the team level. Reduce catastrophic failure rates through collective risk assessment — the tendency to overestimate certainty is countered by the team's disciplined protocol for evaluation under uncertainty.", icon: <Shield className="size-5" />, metric: "0", metricLabel: "Sovereign incidents", accent: "cyan" as const },
  { num: "05", title: "Retention Infrastructure", body: "Construct the conditions where talented people choose to stay. Lower ego friction, transparent communication, and equitable collaboration produce voluntary retention. The five-plus-year average tenure is the evidence that the operating environment sustains performance.", icon: <Trophy className="size-5" />, metric: "5+ yr", metricLabel: "Average tenure", accent: "amber-gold" as const },
  { num: "06", title: "Ego Calibration", body: "Subordinate personal status to collective outcome. Decisions are made faster, conflicts resolve more constructively, and the organization's resources flow toward results rather than politics. The best idea wins, not the loudest voice.", icon: <Target className="size-5" />, metric: "+40%", metricLabel: "Coordination efficiency", accent: "coral" as const },
  { num: "07", title: "Outcome Metrics", body: "Replace activity measurement with outcome measurement. Client satisfaction, operational uptime, security incident rates, campaign success rates, voter reach — the metrics that matter are the metrics that sovereign clients measure.", icon: <TrendingUp className="size-5" />, metric: "100%", metricLabel: "Client satisfaction", accent: "denim" as const },
  { num: "08", title: "Sovereign Validation", body: "Test every claim under conditions where the margin for error is zero. The talent architecture is not aspirational — it is verified across eighteen countries, nine proprietary platforms, and over three hundred elite clients.", icon: <Award className="size-5" />, metric: "18", metricLabel: "Countries", accent: "lime" as const },
];

const PROJECT_STATS = [
  { value: 100, suffix: "%", label: "Client Satisfaction", description: "Across 300+ elite clients", accent: "emerald" as const, icon: <Trophy className="size-5" /> },
  { value: 15, suffix: "/18", label: "Campaign Victories", description: "Eighty-three point three percent", accent: "red" as const, icon: <Target className="size-5" /> },
  { value: 900, suffix: "M+", label: "Voters Reached", description: "Across 22 official languages", accent: "purple" as const, icon: <Users className="size-5" /> },
  { value: 0, label: "Security Incidents", description: "15+ years sovereign-grade", accent: "cyan" as const, icon: <Shield className="size-5" /> },
];

const FAQ_ITEMS = [
  { q: "What is \"The 70% Project\" about?", a: "The signature keynote on the operational evidence that majority-female teams outperform on five measurable dimensions — communication, risk discipline, collaboration, retention, ego management. The argument is drawn from a decade of sovereign-scale operations across eighteen countries. It is not a moral appeal. It is an operating case." },
  { q: "How is this different from other keynotes on women in leadership?", a: "Most keynotes on women in leadership argue from moral obligation, legal compliance, or representation targets. This keynote argues from operations — the evidence that teams built on performance signals rather than demographic assumptions produce measurable, repeatable, superior outcomes. The distinction determines whether the audience resists or adopts." },
  { q: "What are the five operating advantages?", a: "Communication — problems surface earlier because team members say the uncomfortable thing in the room. Risk discipline — higher adherence to protocol without sacrificing judgment. Collaboration — knowledge sharing is consistent, credit is equitable, conflict resolves constructively. Retention — voluntary tenure compounds over years. Ego management — decisions are made faster, results win over politics." },
  { q: "How long is the standard keynote?", a: "Forty-five to sixty minutes for the standard keynote. Fireside chat runs thirty to forty-five minutes. Workshop and masterclass runs two to three hours. Executive briefing runs sixty to ninety minutes. Panel contribution runs twenty to thirty minutes. All formats are available virtually and in person." },
  { q: "Can the workshop be tailored to our hiring architecture?", a: "Yes. The workshop applies the signal-based selection framework directly to the organization's current hiring processes. Tangible outputs include assessment criteria for the five performance signals, an interview protocol calibrated to those criteria, and a team architecture model that operationalizes the talent strategy. The format produces a draft hiring framework ready for refinement and deployment." },
  { q: "What audiences benefit most from this keynote?", a: "HR and talent leadership, diversity and inclusion professionals, C-suite executives evaluating talent strategy, women&apos;s leadership summits, government workforce forums, and corporate retreats. The framework transfers to any organization that depends on team performance as a competitive advantage." },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const FIVE_ADVANTAGES = [
  {
    icon: MessageSquare,
    title: "Communication",
    body: "Problems surface earlier because team members are willing to say the uncomfortable thing in the room before it becomes a catastrophe in the boardroom. Information flows more reliably, handoffs lose less data, escalations reach decision-makers faster.",
    accent: "emerald",
    metric: "Faster",
    metricLabel: "Problem identification",
  },
  {
    icon: Shield,
    title: "Risk Discipline",
    body: "Higher adherence to protocol without sacrificing judgment. The tendency to overestimate certainty, to commit to high-risk courses without sufficient evidence, to escalate rather than mitigate — these tendencies are significantly reduced.",
    accent: "red",
    metric: "Lower",
    metricLabel: "Catastrophic failure rate",
  },
  {
    icon: Users,
    title: "Collaboration",
    body: "Knowledge sharing is more consistent. Credit attribution is more equitable. Conflict resolution is more constructive. The team functions as a coordinated unit rather than a collection of individuals pursuing separate agendas.",
    accent: "purple",
    metric: "+40%",
    metricLabel: "Coordination efficiency",
  },
  {
    icon: TrendingUp,
    title: "Retention",
    body: "The operational environment these teams create is one where talented people choose to stay. Lower ego friction, more transparent communication, and more equitable collaboration produce voluntary retention.",
    accent: "cyan",
    metric: "5+ yr",
    metricLabel: "Average tenure",
  },
  {
    icon: Target,
    title: "Ego Management",
    body: "Decisions are made faster, conflicts are resolved more constructively, and the organization&apos;s resources are directed toward results rather than politics. The best idea wins rather than the loudest voice.",
    accent: "amber-gold",
    metric: "0",
    metricLabel: "Sovereign security incidents",
  },
];

const FIVE_SIGNALS = [
  { title: "Communication Discipline", body: "The ability to transmit information reliably, clearly, and in a timely manner. The foundation of team performance. Evaluated through observed behavior — how a candidate structures information, how they handle follow-up questions, how they respond when the question requires honesty about limitations.", accent: "emerald" },
  { title: "Risk Awareness", body: "The ability to assess uncertainty accurately. Not the confidence to commit — the accuracy of assessment. Teams with strong risk awareness produce fewer catastrophic failures and more measured decision-making under uncertainty.", accent: "red" },
  { title: "Collaborative Depth", body: "The ability to integrate with others, share knowledge equitably, and resolve conflict constructively. The signal that determines whether the team functions as a unit or as a collection of individuals pursuing separate agendas.", accent: "purple" },
  { title: "Retention Stability", body: "The ability to maintain engagement and performance over time. The signal that determines whether the team&apos;s performance compounds or decays. Sustained performance over months and years is the differentiator.", accent: "cyan" },
  { title: "Ego Management", body: "The ability to subordinate personal status to collective outcome. The signal that determines whether the team&apos;s energy is directed toward execution or toward internal politics. Evaluated through behavioral evidence and self-awareness.", accent: "amber-gold" },
];

const EVIDENCE = [
  { metric: "100", suffix: "%", label: "Client Satisfaction", sub: "300+ elite clients", accent: "emerald" },
  { metric: "15/18", suffix: "", label: "Campaign Victories", sub: "Eighty-three point three percent", accent: "red" },
  { metric: "900", suffix: "M+", label: "Voters Reached", sub: "22 official languages", accent: "purple" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years verified", accent: "cyan" },
];

const TAKEAWAYS = [
  { title: "The talent pool has been misallocated by signal, not by supply.", body: "The world does not lack talented women. It lacks organizations that select for the signals women disproportionately demonstrate. Change the signals, change the team.", accent: "emerald" },
  { title: "Diversity programs that argue from morality fail; strategies that argue from operations succeed.", body: "Morality produces compliance. Operations produces adoption. The first action is to reframe the diversity conversation from moral obligation to performance strategy.", accent: "red" },
  { title: "The five operating advantages are measurable.", body: "Communication, risk discipline, collaboration, retention, ego management. The third action is to establish baseline measurements — creating the evidence base for evaluating the impact of signal-based selection.", accent: "purple" },
  { title: "The talent arbitrage is a competitive advantage, not a compliance exercise.", body: "Recruiting from the pool every competitor ignores accesses a reservoir of capability that competitors cannot touch. Audit the organization&apos;s hiring signals — identify where confidence is mistaken for competence.", accent: "cyan" },
  { title: "Build the architecture, not the target.", body: "Teams that outperform are not assembled by demographic target. They are assembled by operational fitness. Design the team architecture that enables diverse teams to perform.", accent: "amber-gold" },
];

const AUDIENCE = [
  { icon: Building2, title: "Diversity & Inclusion Conferences", body: "Diversity professionals and inclusion strategists seeking to move the conversation from moral obligation to operational evidence.", accent: "emerald" },
  { icon: Users, title: "Women&apos;s Leadership Summits", body: "Forums seeking a role model who has built, not just advocated — who demonstrates through operational results that the best operators are the ones the world overlooks.", accent: "coral" },
  { icon: Briefcase, title: "HR & Talent Leadership", body: "Human resources leaders, talent acquisition professionals, and organizational development specialists responsible for hiring strategy, team composition, and performance optimization.", accent: "red" },
  { icon: Crown, title: "Corporate Executive Retreats", body: "C-suite executives and board members evaluating their organization&apos;s talent strategy — who recognize that their competitors are systematically ignoring half the talent pool.", accent: "purple" },
  { icon: Compass, title: "Government Workforce Summits", body: "Government leaders responsible for workforce development, public sector hiring, and institutional diversity — sovereign-scale evidence directly relevant to public policy decisions.", accent: "cyan" },
  { icon: BookOpen, title: "University & Academic Forums", body: "Academic institutions seeking to expose students, faculty, and administrators to the operational evidence for diverse team performance.", accent: "amber-gold" },
];

const FORMATS = [
  { label: "Standard Keynote", duration: "45–60 min", body: "Structured presentation from core argument through the five operating advantages to the evidence base and actionable takeaways.", accent: "emerald" },
  { label: "Fireside Chat", duration: "30–45 min", body: "Conversational exploration of the evidence base guided by a moderator. Effective for HR leadership and women&apos;s leadership gatherings.", accent: "red" },
  { label: "Workshop / Masterclass", duration: "2–3 hrs", body: "Hands-on implementation of signal-based selection. Tangible output: hiring framework, assessment criteria, and team architecture.", accent: "purple" },
  { label: "Panel Contribution", duration: "20–30 min", body: "Focused contribution to multi-speaker discussions on diversity, talent strategy, women&apos;s leadership, or organizational performance.", accent: "cyan" },
  { label: "Executive Briefing", duration: "60–90 min", body: "Private, invitation-only session applying the five operating advantages as a diagnostic tool.", accent: "amber-gold" },
  { label: "Virtual Delivery", duration: "All formats", body: "All formats available for virtual events with the same operational authority and engagement quality.", accent: "denim" },
];

const GAINS = [
  "An operating argument, not a moral appeal — the evidence that compels strategic action",
  "Five measurable operating advantages — communication, risk discipline, collaboration, retention, ego management",
  "The talent arbitrage logic — the structural advantage of recruiting what others ignore",
  "A signal-based selection framework — the methodology that replaces confidence-based selection with performance-based selection",
  "Sovereign-scale evidence — drawn from an operating model verified across eighteen countries and over three hundred elite clients",
  "Immediate applicability — every takeaway designed for implementation starting the next hiring cycle",
];

export default function The70PercentProjectPage() {
  return (
    <>
      <PageHero
        icon={Heart}
        overline="Signature Keynote"
        overlineClassName="text-emerald"
        title={
          <>
            The Operating Case for the Talent <span className="text-gradient-premium">the World Overlooks.</span>
          </>
        }
        subtitle="The 70% Project is the signature keynote on the operational evidence that majority-female teams outperform — not as a moral claim, but as a measured finding, drawn from a decade of sovereign-scale operations across eighteen countries. Five operating advantages. Ten-plus years of verified outcomes. The argument is from operations, not morality."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Speaking", href: "/speaking/" }, { label: "The 70% Project" }]}
        badge="The talent case from operations"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Abstract</span>
          </div>
          <h2 className="text-display-md mb-5">Diversity as operational advantage, not morality.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The central thesis is as simple as it is disruptive: the world&apos;s most overlooked talent pool has been the foundation of everything built. The argument is not from morality. It is from operations. The evidence is a decade of results — fifteen out of eighteen political campaigns won, one-hundred-percent client satisfaction, ninety-nine-point-nine-nine-nine-nine percent operational uptime, zero security incidents.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The keynote dismantles the conventional diversity framework — built on moral obligation, legal compliance, and quota enforcement — and replaces it with an operating framework built on performance evidence. The distinction is not semantic. It is strategic. Morality arguments produce resistance. Operating arguments produce adoption.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Trophy className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Evidence</span>
          </div>
          <h2 className="text-display-md mb-4">A decade of operational data.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The evidence is drawn from sovereign-scale operations serving governments, royal families, political leaders, defense agencies, and Fortune-scale corporations across eighteen countries. Not surveys. Not focus groups. Operational outcomes.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {EVIDENCE.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">The Five Operating Advantages</span>
          </div>
          <h2 className="text-display-md mb-4">What the evidence demonstrates.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Five measurable dimensions of team performance. Each demonstrated through sovereign-scale operations where the consequences of failure are national.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FIVE_ADVANTAGES.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.005] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="flex items-start gap-4 mb-3">
                    <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg ring-2 ${ac.ring} shrink-0`}>
                      <a.icon className="size-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md ${ac.text}`}>{a.title}</h3>
                    </div>
                  </div>
                  <p className="text-body-sm text-muted-foreground leading-relaxed mb-3" dangerouslySetInnerHTML={{ __html: a.body }} />
                  <div className="flex items-baseline gap-2 pt-3 border-t border-border/40">
                    <span className={`text-heading-sm font-bold ${ac.text}`}>{a.metric}</span>
                    <span className="text-caption text-muted-foreground">{a.metricLabel}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Compass className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Signal-Based Selection</span>
          </div>
          <h2 className="text-display-md mb-4">The five signals that predict team performance.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Conventional hiring selects for confidence — self-promotion, visibility, assertiveness. The operating model selects for the signals that actually predict performance.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {FIVE_SIGNALS.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <h3 className={`text-heading-sm mb-2 ${a.text}`}>{s.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Key Takeaways</span>
          </div>
          <h2 className="text-display-md mb-4">Five insights implemented at the next hiring cycle.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The minimum viable operating model for building teams that outperform. Specific, implementable actions.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3 max-w-5xl mx-auto">
          {TAKEAWAYS.map((t, i) => {
            const accents = ["emerald", "red", "purple", "cyan", "amber-gold"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-sm mb-1 ${a.text}`} dangerouslySetInnerHTML={{ __html: t.title }} />
                    <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: t.body }} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Users className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Who This Keynote Serves</span>
          </div>
          <h2 className="text-display-md mb-4">Ideal audience.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The keynote resonates most with decision-makers who recognize that diversity programs built on morality have not produced the outcomes they promised — and who are ready to adopt the operating alternative.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCE.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-sm mb-1.5 ${ac.text}`} dangerouslySetInnerHTML={{ __html: a.title }} />
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Briefcase className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">Format Options</span>
            </div>
            <h2 className="text-display-md mb-4">Flexibility for every event.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Six delivery formats, each calibrated to the audience, the event theme, and the outcomes sought. The customization is structural, not cosmetic.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FORMATS.map((f, i) => {
              const a = ACCENT[f.accent];
              return (
                <motion.div
                  key={f.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className={`text-heading-sm ${a.text}`}>{f.label}</h3>
                    <span className={`text-caption font-bold ${a.text}`}>{f.duration}</span>
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: f.body }} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Award className="size-3.5 text-lime" />
            <span className="text-overline text-lime">What Attendees Gain</span>
          </div>
          <h2 className="text-display-md mb-4">The value of this keynote experience.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {GAINS.map((g, i) => {
            const accents = ["emerald", "red", "purple", "cyan", "amber-gold", "denim"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={g}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <CheckCircle2 className="size-4 text-white" />
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed flex-1">{g}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-coral/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Heart className="size-10 text-emerald mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Book this keynote.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The 70% Project is the talent strategy keynote in the portfolio. The evidence base for building the teams that produce the outcomes every other keynote describes. The argument from operations, not morality.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Book the Keynote
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/the-70-percent-project/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Heart className="size-4" />
                The 70% Project Initiative
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-5`}>
            <Compass className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Signature Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">The 70% Project Pillars.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            Eight pillars form the operating architecture of the talent strategy. Each pillar is a measurable dimension. Each pillar is an implementation component. Each pillar compounds over hiring cycles — the talent arbitrage is structural, not circumstantial.
          </p>
        </div>
        <PillarGridBlock pillars={PROJECT_PILLARS} columns={4} variant="numbered" />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">The Sovereign-Scale Evidence</span>
          </div>
          <h2 className="text-display-md mb-4">Measured outcomes, not surveys.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The argument is from operations. The evidence is structural. The outcomes are measured across sovereign-scale deployments where the margin for error is zero.
          </p>
        </div>
        <CounterBlock stats={PROJECT_STATS} columns={4} />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
            <Flame className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Problem This Keynote Solves</span>
          </div>
          <h2 className="text-display-md mb-4">The talent misallocation that undermines organizational performance.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The failure is endemic across organizations of every size, industry, and geography: the systematic misallocation of talent based on signals that do not predict performance. Leadership teams that do not reflect the talent pool they recruit from. Hiring processes that reward confidence over competence. Performance outcomes that lag behind what a fully optimized talent strategy would produce. The pattern is universal, and the cause is architectural.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The root cause is the confusion of confidence with competence. The signals that most hiring processes evaluate — self-presentation, assertiveness, visibility, self-promotion — predict confidence. They do not reliably predict the capabilities that produce team performance: communication discipline, risk awareness, collaborative depth, retention stability, and ego management. The result is a systematic bias in talent allocation — toward individuals who present well and away from individuals who perform well.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The diversity conversation&apos;s framing compounds the problem. When the talent misallocation is framed as a moral issue, the response is compliance rather than optimization. Organizations implement diversity programs to satisfy moral obligations, not to capture performance advantages. The programs produce demographic changes without performance changes. The talent pool remains underexploited because the argument for exploiting it is moral rather than operational. The keynote solves this by presenting the operating argument. When the signals are corrected — when the selection criteria evaluate communication, risk, collaboration, retention, and ego management — the resulting teams outperform on every measurable dimension.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <AccordionBlock
          items={FAQ_ITEMS}
          eyebrow="Frequently Asked Questions"
          title="What decision-makers ask before booking."
          description="The questions that surface most often in preliminary conversations. Each answer reflects the operating reality, not the marketing narrative."
        />
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Women Breaking Barriers", description: "The individual companion — the operating reality of leading at sovereign scale as a woman.", href: "/speaking/women-breaking-barriers/", icon: Users, accent: "coral" },
          { title: "How Excellence Actually Runs", description: "The operational system that majority-female teams sustain — protocols, checklists, reviews, accountability.", href: "/speaking/how-excellence-actually-runs/", icon: Award, accent: "red" },
          { title: "Leadership as Diagnosis", description: "The clinical skills that sustain diverse leadership teams under pressure.", href: "/speaking/leadership-as-diagnosis/", icon: BookOpen, accent: "purple" },
          { title: "Building Diverse Teams", description: "The operational mechanics — the five signals, communication protocol, retention architecture.", href: "/speaking/building-diverse-teams/", icon: Compass, accent: "cyan" },
        ]}
      />
    </>
  );
}
